type AuthorContent = {
  slug?: string;
  full_name?: string;
  job_title?: string;
  short_bio?: string;
  photo?: { filename?: string };
  url_linkedin?: string;
  expertise_areas?: string[] | { name?: string }[] | string;
  active?: boolean;
  name?: string;
  jobTitle?: string;
  description?: string;
  image?: { filename?: string };
  knowsAbout?: string[] | { name?: string }[] | string;
  isAuthor?: boolean;
};

export type ResolvedAuthorStory = {
  uuid?: string;
  slug?: string;
  content?: AuthorContent;
};

export type AuthorBlok = {
  slug: string;
  full_name: string;
  job_title?: string;
  short_bio?: string;
  photo?: { filename?: string };
  url_linkedin?: string;
  expertise_areas?: string[];
};

const normalizeContributedText = (value: unknown) => {
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
};

const normalizeContributedUrl = (value: unknown) => {
  const trimmed = normalizeContributedText(value);
  if (!trimmed) return undefined;

  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.toString();
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const normalizeContributedPhoto = (
  photo: AuthorContent["photo"] | AuthorContent["image"]
) => {
  const filename = normalizeContributedText(photo?.filename);
  return filename ? { filename } : undefined;
};

export const parseStructuredDataTemplates = (
  value: string | null | undefined
) => value?.split(",").map((t) => t.trim()).filter(Boolean) ?? [];

export const buildAuthorResolveRelations = (components: string[]) =>
  components.map((component) => `${component}.author`).join(",");

export const inlineStoryAuthors = (
  content: { author?: unknown } | null | undefined,
  rels?: ResolvedAuthorStory[]
) => {
  if (!content?.author || !rels?.length) return;

  const relsByUuid = new Map(
    rels
      .filter((rel): rel is ResolvedAuthorStory & { uuid: string } => !!rel.uuid)
      .map((rel) => [rel.uuid, rel])
  );

  const resolve = (entry: unknown) =>
    typeof entry === "string" ? relsByUuid.get(entry) ?? entry : entry;

  content.author = Array.isArray(content.author)
    ? content.author.map(resolve)
    : resolve(content.author);
};

const normalizeExpertiseAreas = (
  areas: AuthorContent["expertise_areas"] | AuthorContent["knowsAbout"]
): string[] | undefined => {
  if (!areas) return undefined;

  if (typeof areas === "string") {
    const value = areas.trim();
    return value ? [value] : undefined;
  }

  if (!areas.length) return undefined;

  const values = areas
    .map((area) => (typeof area === "string" ? area : area?.name))
    .filter(Boolean) as string[];

  return values.length ? values : undefined;
};

const normalizeAuthor = (entry: unknown): AuthorBlok | null => {
  if (!entry || typeof entry !== "object") return null;

  const story = entry as ResolvedAuthorStory;
  const content = story.content ?? (entry as AuthorContent);
  const slug = normalizeContributedText(story.slug ?? content.slug);
  const fullName = normalizeContributedText(content.full_name ?? content.name);
  const jobTitle = normalizeContributedText(content.job_title ?? content.jobTitle);
  const shortBio = normalizeContributedText(content.short_bio ?? content.description);
  const photo = normalizeContributedPhoto(content.photo ?? content.image);
  const urlLinkedin = normalizeContributedUrl(content.url_linkedin);
  const expertiseAreas = content.expertise_areas ?? content.knowsAbout;
  const isInactive = content.active === false || content.isAuthor === false;

  const expertise = normalizeExpertiseAreas(expertiseAreas);

  if (!slug || !fullName || isInactive) {
    return null;
  }

  return {
    slug,
    full_name: fullName,
    ...(jobTitle && { job_title: jobTitle }),
    ...(shortBio && { short_bio: shortBio }),
    ...(photo && { photo }),
    ...(urlLinkedin && { url_linkedin: urlLinkedin }),
    ...(expertise && { expertise_areas: expertise }),
  };
};

export const getActiveAuthors = (authorField: unknown): AuthorBlok[] => {
  if (!authorField) return [];

  const entries = Array.isArray(authorField) ? authorField : [authorField];

  return entries
    .map((entry) => normalizeAuthor(entry))
    .filter((author): author is AuthorBlok => author !== null);
};

export const shouldShowAuthorBlock = (
  component: string | undefined,
  authorOverrideToPersonTemplates: string[],
  authors: AuthorBlok[]
) =>
  !!component &&
  authorOverrideToPersonTemplates.includes(component) &&
  authors.length > 0;

export const buildGraphPersonNode = (author: AuthorBlok, baseURL: string) => ({
  "@type": "Person",
  "@id": `${baseURL}/#/schema/person/${author.slug}`,
  name: author.full_name,
  ...(author.job_title && { jobTitle: author.job_title }),
  ...(author.short_bio && { description: author.short_bio }),
  ...(author.photo?.filename && {
    image: {
      "@type": "ImageObject",
      url: author.photo.filename,
    },
  }),
  ...(author.url_linkedin && { sameAs: [author.url_linkedin] }),
  worksFor: {
    "@id": `${baseURL}/#organization`,
  },
  ...(author.expertise_areas?.length && {
    knowsAbout: author.expertise_areas,
  }),
});

export const buildInlinePersonAuthor = (
  author: AuthorBlok,
  baseURL: string,
  organizationName?: string
) => ({
  "@type": "Person",
  name: author.full_name,
  ...(author.job_title && { jobTitle: author.job_title }),
  ...(author.short_bio && { description: author.short_bio }),
  ...(author.photo?.filename && { image: author.photo.filename }),
  ...(author.url_linkedin && { sameAs: [author.url_linkedin] }),
  worksFor: organizationName
    ? {
        "@type": "Organization",
        name: organizationName,
        url: baseURL,
      }
    : {
        "@id": `${baseURL}/#organization`,
      },
  ...(author.expertise_areas?.length && {
    knowsAbout: author.expertise_areas,
  }),
});

export const buildWebPageAuthorRef = (authors: AuthorBlok[], baseURL: string) => {
  if (!authors.length) return null;

  const refs = authors.map((author) => ({
    "@id": `${baseURL}/#/schema/person/${author.slug}`,
  }));

  return refs.length === 1 ? refs[0] : refs;
};

export const buildArticleAuthorField = (
  authors: AuthorBlok[],
  baseURL: string,
  organizationName?: string
) => {
  if (!authors.length) return null;

  const persons = authors.map((author) =>
    buildInlinePersonAuthor(author, baseURL, organizationName)
  );

  return persons.length === 1 ? persons[0] : persons;
};

export const buildDefaultArticleOrganizationAuthor = (
  baseURL: string,
  organizationName?: string
) => ({
  "@type": "Organization",
  ...(organizationName && { name: organizationName }),
  url: baseURL,
});

type NewsBody = {
  content?: { type?: string; content?: { text?: string }[] }[];
};

export const extractNewsDescription = (body?: NewsBody) => {
  const paragraph = body?.content?.find((item) => item.type === "paragraph");
  return (
    paragraph?.content
      ?.map((item) => item.text)
      .join(" ")
      .trim() ?? ""
  );
};

type GenericNewsContent = {
  title?: string;
  overview_title?: string;
  og_title?: string;
  og_description?: string;
  body?: NewsBody;
  photos?: { asset?: { filename?: string } }[];
  og_image?: { filename?: string };
  date?: string;
  overview_published_date?: string;
};

export const normalizeGenericNewsContent = (content: GenericNewsContent) => {
  const headline =
    content.title ?? content.og_title ?? content.overview_title;
  const description =
    content.og_description || extractNewsDescription(content.body);
  const images = (content.photos ?? [])
    .map((photo) => photo?.asset?.filename)
    .filter(Boolean);

  if (!images.length && content.og_image?.filename) {
    images.push(content.og_image.filename);
  }

  const date = content.date ?? content.overview_published_date;

  return { headline, description, images, date };
};

export const buildNewsArticleStructuredData = (
  content: GenericNewsContent,
  baseURL: string,
  authors: AuthorBlok[],
  organizationName?: string
) => {
  const { headline, description, images, date } =
    normalizeGenericNewsContent(content);
  const articleAuthor =
    buildArticleAuthorField(authors, baseURL, organizationName) ??
    buildDefaultArticleOrganizationAuthor(baseURL, organizationName);

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    ...(headline && { headline }),
    ...(description && { description }),
    ...(images.length && { image: images }),
    ...(date && {
      datePublished: new Date(date).toISOString(),
    }),
    author: articleAuthor,
  };
};
